import { useCallback, useEffect, useState } from "react";
import { Editor } from "slate";
import { ReactEditor } from "slate-react";

interface UseCharacterLimitProps {
  editor: ReactEditor;
  maxCharacters: number;
}

interface UseCharacterLimitResult {
  isLimitReached: boolean;
  checkAndPreventInput: (newTextLength: number, event: { preventDefault: () => void }) => boolean;
  getCurrentLength: () => number;
}

export const useCharacterLimit = ({
  editor,
  maxCharacters,
}: UseCharacterLimitProps): UseCharacterLimitResult => {
  const [isLimitReached, setIsLimitReached] = useState(false);

  const getCurrentLength = useCallback(() => {
    return Editor.string(editor, []).length;
  }, [editor]);

  const checkAndPreventInput = useCallback(
    (newTextLength: number, event: { preventDefault: () => void }): boolean => {
      const currentLength = getCurrentLength();
      const wouldExceedLimit = currentLength + newTextLength > maxCharacters;

      if (wouldExceedLimit) {
        event.preventDefault();
        setIsLimitReached(true);
        return false;
      }

      setIsLimitReached(false);
      return true;
    },
    [getCurrentLength, maxCharacters]
  );

  // Update isLimitReached whenever content changes
  useEffect(() => {
    const currentLength = getCurrentLength();
    setIsLimitReached(currentLength >= maxCharacters);
  }, [getCurrentLength, maxCharacters]);

  return {
    isLimitReached,
    checkAndPreventInput,
    getCurrentLength,
  };
};
